import { useMemo, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import { getAccessiblePaletteOptions, interpolatePalette, BLUES_PALETTE, REDS_PALETTE, GREENS_PALETTE, buildPaletteFromPrimary } from '../../charts/shared/palettes';
import transformData from '../../utils/transformData';
import { isExternalColorControl } from '../../config/deploymentProfile';
import { externalColorControl, externalBackgroundControl, CONTROL_SECTIONS, CONTROL_SECTION_MAP, CONDITIONAL_CONTROLS, SECTION_THEME_SLIDERS } from '../../charts/shared/controls';
import { ASPECT_RATIO_PRESETS } from '../ChartPreview';

import ThemeSection from './ThemeSection';
import ControlSection from './ControlSection';
import ThemeSlider from './ThemeSlider';
import ColorMapSection from './ColorMapSection';
import { LockedColorModeControl, ExternalColorControl, ExternalBackgroundControl } from './ExternalControls';
import { computeColorMapItems } from './colorMapUtils';

// Controls that only apply to sample data generation
const SAMPLE_DATA_CONTROLS = ['numCategories', 'numSeries', 'numStacks'];

function ControlsPanel({ chartType, config, onChange: onChangeRaw }) {
  const { isLoaded, derivedData: rawData, mapping } = useData();
  const { isLocked, resolved: theme, deploymentProfile: themePolicy, themeId } = useTheme();
  const ui = theme.ui || {};

  // Wrap onChange to enforce exclusive groups: when a control in an exclusive
  // group is activated, sibling controls in the same group are auto-reset.
  const onChange = useCallback((key, value) => {
    const allControls = chartType.controls;
    const changedControl = allControls.find(c => c.key === key);

    // For palette toggles, the key is nested inside the paletteGroup control
    const paletteParent = allControls.find(c =>
      c.type === 'paletteGroup' && c.options?.some(o => o.key === key)
    );

    if (paletteParent?.exclusiveGroup && value === true) {
      // Activating a palette toggle — deactivate sibling palette toggles
      paletteParent.options.forEach(o => {
        if (o.key !== key) onChangeRaw(o.key, false);
      });
      // Also reset sibling controls in the same exclusive group (e.g. single color)
      allControls.forEach(c => {
        if (c.exclusiveGroup === paletteParent.exclusiveGroup && c.key !== paletteParent.key) {
          // Don't clear color — it's the fallback. Just clear palette flags.
        }
      });
    } else if (changedControl?.exclusiveGroup) {
      // Activating a non-palette control in the group — deactivate palette toggles
      allControls.forEach(c => {
        if (c.exclusiveGroup === changedControl.exclusiveGroup && c.key !== key && c.type === 'paletteGroup') {
          c.options?.forEach(o => onChangeRaw(o.key, false));
        }
      });
    }

    onChangeRaw(key, value);
  }, [chartType.controls, onChangeRaw]);

  const isAcademicInternal = themePolicy.isInternal && themeId === 'academic';

  // Seed primaryColor when entering Academic theme; clear it when leaving (so other themes use their own palette)
  useEffect(() => {
    if (isAcademicInternal && !config.primaryColor && theme.palette?.[0]) {
      onChange('primaryColor', theme.palette[0]);
    } else if (!isAcademicInternal && config.primaryColor) {
      onChange('primaryColor', undefined);
    }
  }, [isAcademicInternal]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleControls = chartType.controls.filter(c => {
    if (isLoaded && SAMPLE_DATA_CONTROLS.includes(c.key)) return false;
    if ((themePolicy.isExternal || isAcademicInternal) && isExternalColorControl(c)) return false;
    if (isLocked && (c.type === 'color' || c.type === 'paletteGroup')) return false;
    return true;
  });

  const accessibleControls = visibleControls
    .map((control) => {
      if (control.type !== 'paletteGroup') return control;
      return {
        ...control,
        options: getAccessiblePaletteOptions(control.options, theme),
      };
    })
    .filter((control) => control.type !== 'paletteGroup' || control.options.length > 0);

  const externalColorControlWithToggle = {
    ...externalColorControl,
    showModeToggle: 'externalColorMode' in (chartType?.defaultConfig ?? {}),
    ...(isAcademicInternal && {
      presetColors: (theme.palette || []).map((value, i) => ({ value, label: `Color ${i + 1}` })),
    }),
  };

  const lockedColorModeControl = { key: 'lockedColorMode', label: 'Color Mode', type: 'lockedColorMode' };

  const controlsToRender = themePolicy.isExternal
    ? [externalBackgroundControl, externalColorControlWithToggle, ...accessibleControls]
    : isAcademicInternal
      ? [externalBackgroundControl, externalColorControlWithToggle, ...accessibleControls]
      : isLocked
        ? [lockedColorModeControl, ...accessibleControls]
        : accessibleControls;

  const colorMapData = useMemo(() => {
    if (!isLoaded || !rawData?.length || !mapping) return null;
    return transformData(rawData, mapping, chartType.id);
  }, [isLoaded, rawData, mapping, chartType.id]);

  const colorMapItems = useMemo(() => {
    if (!colorMapData) return [];
    return computeColorMapItems(chartType.id, config, colorMapData, mapping, theme);
  }, [chartType.id, config, colorMapData, mapping, theme]);

  const allChartControlKeys = chartType.controls.map(c => c.key);
  const hasColorByData = colorMapData?.[0]?._colorBy != null;

  // Controls hidden when colorBy IS active (replaced by palette controls)
  const COLOR_BY_HIDES = new Set(['positiveColor', 'negativeColor']);
  // Controls that only appear when colorBy IS active on charts that normally
  // use positiveColor/negativeColor (e.g. diverging bar)
  const COLOR_BY_ONLY = new Set(['color', 'paletteGroup']);
  const hasPosNegControls = allChartControlKeys.includes('positiveColor');

  const sectionControls = {};
  const ungroupedControls = [];
  controlsToRender.forEach(control => {
    if (hasColorByData && COLOR_BY_HIDES.has(control.key)) return;
    if (!hasColorByData && hasPosNegControls && COLOR_BY_ONLY.has(control.key)) return;

    const toggleKey = CONDITIONAL_CONTROLS[control.key];
    if (toggleKey) {
      const toggleExists = allChartControlKeys.includes(toggleKey);
      if (toggleExists && !config[toggleKey]) return;
    }

    const sectionId = CONTROL_SECTION_MAP[control.key];
    if (sectionId) {
      if (!sectionControls[sectionId]) sectionControls[sectionId] = [];
      sectionControls[sectionId].push(control);
    } else {
      ungroupedControls.push(control);
    }
  });

  const handleColorOverride = (itemKey, color) => {
    const prev = config.colorOverrides || {};
    if (color === null) {
      const { [itemKey]: _, ...rest } = prev;
      onChange('colorOverrides', Object.keys(rest).length > 0 ? rest : undefined);
    } else {
      onChange('colorOverrides', { ...prev, [itemKey]: color });
    }
  };

  const handleResetAllColors = () => {
    onChange('colorOverrides', undefined);
  };

  const activePaletteSwatches = useMemo(() => {
    const count = 8;
    if (config.primaryColor) return interpolatePalette(buildPaletteFromPrimary(config.primaryColor), count);
    if (config.useBluesPalette) return interpolatePalette(BLUES_PALETTE, count);
    if (config.useRedsPalette) return interpolatePalette(REDS_PALETTE, count);
    if (config.useGreensPalette) return interpolatePalette(GREENS_PALETTE, count);
    if (theme?.palette?.length) return interpolatePalette(theme.palette, count);
    if (hasColorByData) return interpolatePalette(BLUES_PALETTE, count);
    return [
      ...BLUES_PALETTE.slice(1, 3),
      ...REDS_PALETTE.slice(1, 3),
      ...GREENS_PALETTE.slice(1, 3),
    ];
  }, [config.primaryColor, config.useBluesPalette, config.useRedsPalette, config.useGreensPalette, theme?.palette, hasColorByData]);

  return (
    <div
      className="w-64 border-r flex flex-col h-screen sticky top-0 flex-shrink-0 ui-bg ui-border"
      style={{ backgroundColor: ui.bg, borderRightColor: ui.border, color: ui.text }}
    >
      <div
        className="h-12 px-4 border-b flex items-center ui-border"
        style={{ borderBottomColor: ui.border }}
      >
        <h2 className="text-sm font-medium">{chartType.name}</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Layout section */}
        <div className="mb-4 pb-4 border-b" style={{ borderBottomColor: ui.border }}>
          <h3 className="text-xs font-medium mb-2" style={{ color: ui.textSecondary }}>Layout</h3>
          <div>
            <label className="block text-xs mb-1" style={{ color: ui.textSecondary }}>
              Canvas Shape
            </label>
            <select
              value={config.aspectRatio || 'landscape'}
              onChange={(e) => onChange('aspectRatio', e.target.value)}
              className="w-full text-xs border py-1 px-2 ui-border"
              style={{ borderColor: ui.border, backgroundColor: ui.bg, color: ui.text }}
            >
              {Object.entries(ASPECT_RATIO_PRESETS).map(([key, preset]) => (
                <option key={key} value={key}>
                  {preset.label} ({preset.width}&times;{preset.height})
                </option>
              ))}
            </select>
          </div>
        </div>

        <ThemeSection showThemeControls={themePolicy.showThemeSection} />

        {ungroupedControls.length > 0 && (
          <div className="space-y-3 pb-3 mb-1 border-b" style={{ borderBottomColor: ui.borderSecondary }}>
            {ungroupedControls.map((control) => (
              <ControlRenderer
                key={control.key}
                control={control}
                value={config[control.key]}
                config={config}
                onChange={onChange}
                ui={ui}
              />
            ))}
          </div>
        )}

        {CONTROL_SECTIONS.map(section => {
          const controls = sectionControls[section.id] || [];
          const showColorMap = section.id === 'color' && colorMapItems.length >= 1;
          const showColorByBanner = section.id === 'color' && hasColorByData && mapping?.colorBy;
          const themeSliders = (SECTION_THEME_SLIDERS[section.id] || []).filter(s => {
            if (!s.requires) return true;
            if (!allChartControlKeys.includes(s.requires)) return false;
            return !!config[s.requires];
          });
          if (controls.length === 0 && !showColorMap && !showColorByBanner && themeSliders.length === 0) return null;
          return (
            <ControlSection key={section.id} label={section.label} defaultExpanded={section.defaultExpanded} ui={ui}>
              <div className="space-y-3">
                {showColorByBanner && (
                  <div
                    className="text-xs px-2 py-1.5 border rounded"
                    style={{ borderColor: ui.borderSecondary, backgroundColor: ui.bgSecondary || ui.bg, color: ui.textSecondary }}
                  >
                    Coloring by <strong style={{ color: ui.text }}>{mapping.colorBy}</strong>
                  </div>
                )}
                {controls.map((control) => (
                  <ControlRenderer
                    key={control.key}
                    control={control}
                    value={config[control.key]}
                    config={config}
                    onChange={onChange}
                    ui={ui}
                  />
                ))}
                {showColorMap && (
                  <ColorMapSection
                    items={colorMapItems}
                    overrides={config.colorOverrides || {}}
                    onChange={handleColorOverride}
                    onReset={handleResetAllColors}
                    ui={ui}
                    paletteColors={activePaletteSwatches}
                  />
                )}
                {themeSliders.map(s => (
                  <ThemeSlider
                    key={s.key}
                    themeKey={s.key}
                    label={s.label}
                    min={s.min}
                    max={s.max}
                    defaultVal={s.default}
                  />
                ))}
              </div>
            </ControlSection>
          );
        })}
      </div>
    </div>
  );
}

function ControlRenderer({ control, value, config, onChange, ui = {} }) {
  switch (control.type) {
    case 'toggle':
      return (
        <label className="flex items-center gap-3 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => onChange(control.key, e.target.checked)}
          />
          <span>{control.label}</span>
        </label>
      );

    case 'text':
      return (
        <div>
          <label className="block text-xs mb-1" style={{ color: ui.textSecondary }}>
            {control.label}
          </label>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(control.key, e.target.value)}
            placeholder={control.placeholder || ''}
          />
        </div>
      );

    case 'slider': {
      const displayValue = value != null ? `${value}${control.unit || ''}` : 'Auto';
      return (
        <div>
          <label className="block text-xs mb-1" style={{ color: ui.textSecondary }}>
            {control.label}: {displayValue}
          </label>
          <input
            type="range"
            min={control.min}
            max={control.max}
            step={control.step}
            value={value ?? control.min}
            onChange={(e) => onChange(control.key, Number(e.target.value))}
          />
        </div>
      );
    }

    case 'select':
      return (
        <div>
          <label className="block text-xs mb-1" style={{ color: ui.textSecondary }}>
            {control.label}
          </label>
          <select
            value={value || ''}
            onChange={(e) => onChange(control.key, e.target.value)}
          >
            {control.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );

    case 'color':
      return (
        <div>
          <label className="block text-xs mb-2" style={{ color: ui.textSecondary }}>
            {control.label}
          </label>
          <div className="flex flex-wrap gap-2">
            {control.options.map((color) => (
              <button
                key={color}
                onClick={() => onChange(control.key, color)}
                className="w-6 h-6 border-2"
                style={{
                  backgroundColor: color,
                  borderColor: value === color ? ui.border : ui.borderSecondary,
                }}
                title={color}
              />
            ))}
          </div>
        </div>
      );

    case 'lockedColorMode':
      return <LockedColorModeControl config={config} onChange={onChange} ui={ui} />;

    case 'externalColor':
      return <ExternalColorControl control={control} config={config} onChange={onChange} ui={ui} />;

    case 'externalBackground':
      return <ExternalBackgroundControl control={control} config={config} onChange={onChange} ui={ui} />;

    case 'paletteGroup':
      return (
        <div>
          <label className="block text-xs mb-2" style={{ color: ui.textSecondary }}>
            {control.label}
          </label>
          <div className="space-y-2">
            {control.options.map((option) => (
              <label key={option.key} className="flex items-center gap-3 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={config[option.key] || false}
                  onChange={(e) => onChange(option.key, e.target.checked)}
                />
                <span className="w-16">{option.label}</span>
                {option.colors && (
                  <div className="flex gap-0.5">
                    {option.colors.map((color, idx) => (
                      <div key={idx} className="w-3 h-3" style={{ backgroundColor: color }} />
                    ))}
                  </div>
                )}
              </label>
            ))}
          </div>
        </div>
      );

    default:
      return null;
  }
}

export default ControlsPanel;
